import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CustomDatePicker } from '@components/CustomDatePicker';
import { BasePage } from './BasePage';
import { useSelector } from 'react-redux';
import { ScrollToTop } from '@components/ScrollToTop';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';

import type { RootState } from '@features/redux/store';

interface BaseReportPageProps {
    children: React.ReactNode;
    title: string;
    dateRange?: { startDate: string; endDate: string };
    setDateRange?: (value: { startDate: string; endDate: string }) => void;
}

export const BaseReportPage = ({ children, dateRange, title, setDateRange }: BaseReportPageProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { LastUpdated } = useSelector((state: RootState) => state.report);

    return (
        <BasePage>
            <ScrollToTop />
            <BreadcrumbSubBar />

            {/* Page content container - scrollable */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: colors.neutral[100],
                    p: isMobile ? 1.5 : 2,
                }}>
                {/* Page Header Card */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        backgroundColor: colors.neutral[0],
                        borderRadius: `${radii.lg}px`,
                        border: `1px solid ${colors.neutral[200]}`,
                        p: isMobile ? 2 : 2.5,
                        mb: 2,
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: isMobile ? typographyTokens.fontSize.xl : typographyTokens.fontSize['2xl'],
                                fontWeight: typographyTokens.fontWeight.bold,
                                color: colors.neutral[900],
                            }}>
                            {title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[500],
                                mt: 0.5,
                            }}>
                            Last updated {LastUpdated}
                        </Typography>
                    </Box>

                    {/* Date Range Picker */}
                    {dateRange && setDateRange && (
                        <Box sx={{ flexShrink: 0 }}>
                            <CustomDatePicker dateRange={dateRange} setDateRange={setDateRange} showSpan />
                        </Box>
                    )}
                </Box>

                {/* Report Content - renders multiple report components */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}>
                    {children}
                </Box>
            </Box>
        </BasePage>
    );
};
